import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { OutboundMessage } from '../types/outboundMessage';
import { stableStringify } from '@Utils/searchParams';

type OutboundMessageResponse = {
  data: OutboundMessage[];
  meta: Record<string, any>;
};
type DefaultError = unknown;

type UseOutboundMessageParams<
  S extends Record<string, any> = Record<string, any>,
> = {
  /** Optional query string params to forward to service.getOutboundMessages */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;
};

export function useOutboundMessage<
  TData = OutboundMessageResponse,
  TError = DefaultError,
>(
  { searchParams, enabled = true }: UseOutboundMessageParams = {},
  options?: Omit<
    UseQueryOptions<OutboundMessageResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  return useQuery<OutboundMessageResponse, TError, TData>({
    queryKey: ['messages', stableParams],
    enabled: enabled,
    queryFn: async () => {
      // membersId is truthy when enabled is true (see enabled gate above)
      return service.getOutboundMessages(searchParams ?? {});
    },
    // caller can pass select, staleTime, gcTime, retry, refetchOnWindowFocus, etc.
    ...(options as any),
  });
}
